import {
  createSlice,
  PayloadAction,
  ActionReducerMapBuilder,
} from "@reduxjs/toolkit";
import {
  fetchAllSelectorExperiences,
  fetchSelectorExperiencesForOrganization,
  fetchSelectorExperience,
  createSelectorExperience,
  updateSelectorExperience,
  deleteSelectorExperience,
} from "../actions/selectorExperienceActions";

export interface ExperienceEntry {
  title: string;
  subtitle: string | null;
  res_link_params_id: string;
}

export interface SelectorExperience {
  selector_id: string;
  organization_id: string;
  pre_selection_copy: string | null;
  selector_title: string | null;
  selector_subtitle: string | null;
  entries: ExperienceEntry[];
  sub_client_mode_enabled: boolean;
}

interface SelectorExperienceState {
  selectorExperiences: SelectorExperience[];
  currentSelectorExperience: SelectorExperience | null;
  loading: boolean;
  error: string | null;
}

const initialState: SelectorExperienceState = {
  selectorExperiences: [],
  currentSelectorExperience: null,
  loading: false,
  error: null,
};

const selectorExperienceSlice = createSlice({
  name: "selectorExperiences",
  initialState,
  reducers: {
    clearCurrentSelectorExperience(state) {
      state.currentSelectorExperience = null;
    },
  },
  extraReducers: (builder: ActionReducerMapBuilder<SelectorExperienceState>) => {
    // Handle fetchAllSelectorExperiences
    builder
      .addCase(fetchAllSelectorExperiences.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchAllSelectorExperiences.fulfilled,
        (state, action: PayloadAction<SelectorExperience[]>) => {
          state.loading = false;
          state.selectorExperiences = action.payload;
        }
      )
      .addCase(fetchAllSelectorExperiences.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch selector experiences";
      });

    // Handle fetchSelectorExperiencesForOrganization
    builder
      .addCase(fetchSelectorExperiencesForOrganization.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchSelectorExperiencesForOrganization.fulfilled,
        (state, action: PayloadAction<SelectorExperience[]>) => {
          state.loading = false;
          state.selectorExperiences = action.payload;
        }
      )
      .addCase(fetchSelectorExperiencesForOrganization.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch selector experiences";
      });

    // Handle fetchSelectorExperience
    builder
      .addCase(fetchSelectorExperience.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchSelectorExperience.fulfilled,
        (state, action: PayloadAction<SelectorExperience>) => {
          state.loading = false;
          state.currentSelectorExperience = action.payload;
        }
      )
      .addCase(fetchSelectorExperience.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch selector experience";
      });

    // Handle createSelectorExperience
    builder
      .addCase(createSelectorExperience.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        createSelectorExperience.fulfilled,
        (state, action: PayloadAction<SelectorExperience>) => {
          state.loading = false;
          state.currentSelectorExperience = action.payload;
          state.selectorExperiences.unshift(action.payload);
        }
      )
      .addCase(createSelectorExperience.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to create selector experience";
      });

    // Handle updateSelectorExperience
    builder
      .addCase(updateSelectorExperience.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        updateSelectorExperience.fulfilled,
        (state, action: PayloadAction<SelectorExperience>) => {
          state.loading = false;
          state.currentSelectorExperience = action.payload;
          const index = state.selectorExperiences.findIndex(
            (se) => se.selector_id === action.payload.selector_id
          );
          if (index !== -1) {
            state.selectorExperiences[index] = action.payload;
          }
        }
      )
      .addCase(updateSelectorExperience.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to update selector experience";
      });

    // Handle deleteSelectorExperience
    builder
      .addCase(deleteSelectorExperience.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        deleteSelectorExperience.fulfilled,
        (state, action: PayloadAction<string>) => {
          state.loading = false;
          state.selectorExperiences = state.selectorExperiences.filter(
            (se) => se.selector_id !== action.payload
          );
          if (state.currentSelectorExperience?.selector_id === action.payload) {
            state.currentSelectorExperience = null;
          }
        }
      )
      .addCase(deleteSelectorExperience.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to delete selector experience";
      });
  },
});

export const { clearCurrentSelectorExperience } = selectorExperienceSlice.actions;
export default selectorExperienceSlice.reducer;
